/**
 * WebSocket client for WS-Print (/ws/print)
 * Builds URL from Vite env (`VITE_API_URL`) and handles identification headers.
 */

export function buildWsPrintUrl() {
  try {
    const apiBase = import.meta.env?.VITE_API_URL;
    if (apiBase) {
      const u = new URL(apiBase);
      const scheme = u.protocol === 'https:' ? 'wss' : 'ws';
      // Strip trailing '/api' or '/v1'
      const basePath = u.pathname.replace(/\/?api(\/v1)?\/?$/, '').replace(/\/$/, '');
      const url = `${scheme}://${u.hostname}${u.port ? `:${u.port}` : ''}${basePath}/ws/print`;
      return url;
    }
  } catch { void 0 }

  // Fallback to current origin
  const loc = window.location;
  const scheme = loc.protocol === 'https:' ? 'wss' : 'ws';
  const url = `${scheme}://${loc.host}/ws/print`;

  return url;
}

class WsPrintClient {
  constructor() {
    this.websocket = null;
    this._connecting = false;
    this._reconnectDelayMs = 3000;
    this.ident = null;
    this.printerName = null;
    this.uuid = null;
    this.pairedWith = null;
    this._debug = true;
    this._log = (level, ...args) => { try { (console[level] || console.log).call(console, '[WS-PRINT]', ...args); } catch { } };
    this.callbacks = {
      onOpen: null,
      onClose: null,
      onError: null,
      onMessage: null,
      onStatus: null, // generic status updates
      onPrinter: null, // printer selected/updated
      onUnpaired: null, // despareado
      onPrintResult: null, // retorno_impressao_ok|error
      onPrinters: null, // lista de impressoras
    };
  }

  setCallbacks(cb) {
    this.callbacks = { ...this.callbacks, ...cb };
  }

  get isConnected() {
    return !!this.websocket && this.websocket.readyState === WebSocket.OPEN;
  }

  get isConnecting() {
    return !!this.websocket && this.websocket.readyState === WebSocket.CONNECTING;
  }

  async initializeWebsocket(ident) {
    this.ident = ident;
    this.uuid = ident?.id;
    const url = buildWsPrintUrl();
    if (this._debug) this._log('debug', 'Inicializando conexão', { url, ident });

    return new Promise((resolve, reject) => {
      try {
        if (this.websocket && (this.isConnected || this.isConnecting)) {
          try {
            if (ident?.id) {
              this.websocket.send(JSON.stringify({ uuid: ident.id }));
              if (this._debug) this._log('debug', 'Reenviei uuid em conexão existente', { uuid: ident.id });
            }
          } catch { }
          if (ident?.pairWithUuid) {
            this.pairWith(ident.pairWithUuid);
          }
          resolve(true);
          return;
        }

        const coockiHeaders = {
          empresa: ident?.branches?.sigla ?? ident?.empresa ?? '',
          id: ident?.id,
          authorization: import.meta.env?.VITE_PRINT_SECRET,
          server: false,
          computer: ident?.computer,
          nome: ident?.codigo ?? ident?.nome ?? '',
          usuario: ident?.name ?? ident?.usuario ?? '',
          lote: '',
          emparelhar: ident?.pairWithUuid || '',
          versao: 'on board',
        };

        this._connecting = true;
        try {
          this.websocket = new WebSocket(url + '?coockie=' + encodeURIComponent(JSON.stringify(coockiHeaders)));
        } catch {
          this._connecting = false;
          return;
        }
        if (this._debug) this._log('debug', 'WebSocket criado, aguardando onopen');

        this.websocket.onopen = () => {
          if (this._debug) this._log('info', 'Conexão aberta', { url });
          this._connecting = false;
          try {
            // Também envia headers em formato compatível com wsServer
            const headersMsg = JSON.stringify({ headers: coockiHeaders });
            this.websocket.send(headersMsg);

            if (ident?.id) {
              const uuidMsg = JSON.stringify({ uuid: ident.id });
              this.websocket.send(uuidMsg);
              if (this._debug) this._log('debug', 'UUID enviado', { uuid: ident.id });
            }
          } catch {
            console.warn('Falha ao enviar headers/uuid');
          }

          if (this.callbacks.onOpen) this.callbacks.onOpen({ url });
        };

        this.websocket.onerror = (error) => {
          this._connecting = false;
          if (this._debug) this._log('error', 'Erro na conexão', error);
          if (this.callbacks.onError) this.callbacks.onError(error);
          reject(error);
        };

        this.websocket.onclose = (event) => {
          this._connecting = false;
          if (this._debug) this._log('warn', 'Conexão fechada', { code: event.code, reason: event.reason });
          if (this.callbacks.onClose) this.callbacks.onClose(event);
          if (this.callbacks.onStatus) this.callbacks.onStatus({ connected: false });
          // Auto-reconnect
          setTimeout(() => {
            if (!this.isConnected && this.ident) {
              this.initializeWebsocket(this.ident).catch(() => { });
            }
          }, this._reconnectDelayMs);
        };

        this.websocket.onmessage = (event) => {
          const data = event.data;
          if (this._debug) this._log('debug', 'Mensagem recebida', data);

          if (data === 'ping') {
            try { this.websocket.send('pong'); } catch { }
            return;
          }

          let payload;
          try {
            payload = JSON.parse(data);
          } catch {
            payload = data;
          }

          if (typeof payload === 'string') {
            if (payload === 'retorno_impressao_ok' || payload === 'retorno_impressao_error') {
              if (this.callbacks.onPrintResult) {
                this.callbacks.onPrintResult({
                  success: payload === 'retorno_impressao_ok',
                  status: payload,
                });
              }
            }
            return;
          }

          if (!payload || typeof payload !== 'object') return;

          const firstKey = Object.keys(payload)[0];
          switch (firstKey) {
            case 'uuid':
              if (this._debug) this._log('info', 'UUID confirmado', payload.uuid);
              this.uuid = payload.uuid;
              if (this.callbacks.onStatus) this.callbacks.onStatus({ connected: true, uuid: payload.uuid });
              resolve(true);
              {
                const targetPair = ident?.pairWithUuid || this.pairedWith || (typeof localStorage !== 'undefined' ? (localStorage.getItem('wsPrintPairedWith') || localStorage.getItem('printerDevice')) : null);
                if (targetPair) {
                  this.pairWith(targetPair);
                }
              }
              break;

            case 'parear':
            case 'pareado':
              if (this._debug) this._log('info', 'PAREADO confirmado', payload[firstKey]);
              this.pairedWith = payload[firstKey];
              try { if (typeof localStorage !== 'undefined' && this.pairedWith) localStorage.setItem('wsPrintPairedWith', String(this.pairedWith)); } catch { }
              if (this.callbacks.onStatus) this.callbacks.onStatus({ connected: true, paired: true, pairedWith: payload[firstKey] });
              break;

            case 'no_paredo':
            case 'no_pareado':
            case 'unpaired':
              this.pairedWith = null;
              if (this.callbacks.onUnpaired) this.callbacks.onUnpaired(payload);
              if (this.callbacks.onStatus) this.callbacks.onStatus({ connected: true, paired: false });
              break;

            case 'impressora':
            case 'sel_impressora':
              if (payload.nome) {
                this.printerName = payload.nome;
                if (this.callbacks.onPrinter) this.callbacks.onPrinter(payload.nome);
              }
              break;

            case 'impressoras':
              if (this.callbacks.onPrinters) this.callbacks.onPrinters(payload.impressoras || payload[firstKey]);
              break;

            case 'retorno_impressao_ok':
            case 'retorno_impressao_error':
              if (this.callbacks.onPrintResult) {
                this.callbacks.onPrintResult({
                  success: firstKey === 'retorno_impressao_ok',
                  ...payload,
                });
              }
              break;

            default:
              if (this._debug) this._log('debug', 'Mensagem JSON recebida', payload);
              if (this.callbacks.onMessage) this.callbacks.onMessage(payload);
          }
        };

      } catch (error) {
        this._connecting = false;
        if (this._debug) this._log('error', 'Falha ao inicializar WebSocket', error);
        if (this.callbacks.onError) this.callbacks.onError(error);
        reject(error);
      }
    });
  }

  emit(objOrString) {
    try {
      if (!this.isConnected) throw new Error('WebSocket não está aberto');
      const payload = typeof objOrString === 'string' ? objOrString : JSON.stringify(objOrString);
      if (this._debug) {
        try {
          this._log('debug', 'Emitindo mensagem', objOrString);
        } catch { }
      }
      this.websocket.send(payload);
    } catch (e) {
      if (this._debug) this._log('error', 'Falha ao emitir mensagem', e);
      if (this.callbacks.onError) this.callbacks.onError(e);
    }
  }

  pairWith(destUuid) {
    try {
      if (!this.isConnected) throw new Error('WebSocket não está aberto');
      const originId = this.ident?.id;
      if (!originId) throw new Error('UUID do cliente não definido');
      const msg = { parear: originId, uuid: destUuid };
      if (this._debug) this._log('info', 'Pareamento solicitado via API', msg);
      this.websocket.send(JSON.stringify(msg));
    } catch (e) {
      if (this._debug) this._log('error', 'Falha ao parear', e);
      if (this.callbacks.onError) this.callbacks.onError(e);
    }
  }

  close() {
    try {
      if (this.websocket) this.websocket.close();
    } catch { }
    this.websocket = null;
    this._connecting = false;
    this.pairedWith = null;
    this.printerName = null;
  }
}

const wsPrintClient = new WsPrintClient();
export default wsPrintClient;
export { WsPrintClient };
export const printerName = () => wsPrintClient.printerName;